import * as React from 'react';
import {
  Compass,
  Building2,
  ShoppingBag,
  Wallet,
  Gem,
  Settings,
  LayoutDashboard,
  type LucideIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink, useLocation } from 'react-router-dom';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
} from '@/components/ui/Sidebar';
import { NavUser } from '@/components/NavUser/NavUser';
import { links } from '@/constants/links';
import { FINAL_USERS, ORG_USERS } from '@/enums';
import { PermissionName } from '@/types';
import { Link } from '@/types/types';
import { useGetUser } from '@/hooks/useGetUser';

const NAV_ICON_MAP: Record<string, LucideIcon> = {
  Compass,
  Building: Building2,
  Bag: ShoppingBag,
  Finances: Wallet,
  Ruby: Gem,
  Settings,
};

function getNavIcon(type: string): LucideIcon {
  return NAV_ICON_MAP[type] ?? LayoutDashboard;
}

export function AppSidebar(props: React.ComponentProps<typeof Sidebar>) {
  const { t } = useTranslation();
  const location = useLocation();
  const { currentUser } = useGetUser();

  const permittedLinks = React.useMemo(() => {
    let data = links['cdk'];
    if (ORG_USERS.includes(currentUser?.type)) {
      data = data.filter((link) => link.permission !== PermissionName.ORGANIZATION);
    }
    if (FINAL_USERS.includes(currentUser?.type)) {
      data = data.filter((link) => link.permission !== PermissionName.PLANS);
    }
    return data as Link[];
  }, [currentUser?.type]);

  return (
    <Sidebar collapsible='icon' {...props}>
      <SidebarHeader>
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton size='lg' asChild tooltip={t('Sidebar.scale')}>
              <NavLink to='/cdk/dashboard' className='flex items-center gap-2'>
                <img
                  src='/Scale.svg'
                  alt={t('Sidebar.scale')}
                  className='h-8 w-8 rounded-[8px]'
                  draggable={false}
                />
                <span className='truncate text-base font-semibold'>{t('Sidebar.scale')}</span>
              </NavLink>
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarHeader>

      <SidebarSeparator />

      <SidebarContent>
        <SidebarGroup>
          <SidebarGroupLabel>{t('Navigation.general')}</SidebarGroupLabel>
          <SidebarMenu>
            {permittedLinks.map((link) => {
              const isActive =
                location.pathname === link.href || location.pathname.startsWith(link.href + '/');
              const IconComponent = getNavIcon(link?.type ?? '');

              return (
                <SidebarMenuItem key={link.href}>
                  <SidebarMenuButton
                    asChild
                    isActive={isActive}
                    tooltip={t(`Navigation.${link.text}`)}
                  >
                    <NavLink to={link.href ?? '/'}>
                      <IconComponent className='size-4 shrink-0' aria-hidden />
                      <span>{t(`Navigation.${link.text}`)}</span>
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              );
            })}
          </SidebarMenu>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter>
        <NavUser />
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
